import { fold } from '@blpde/nx-ui';

/**
 * One module's `GET /v1/search/suggest` body (I-3024). `tier` 1 account,
 * 2 company, 3 tenant, 4 module, 5 base. `reason` `correction` or `synonym`
 * marks a direct suggestion. `distance` is 0 for a direct suggestion; a
 * module that does not send it yet is treated as 1.
 */
export interface SuggestionWire {
  text?: string;
  source?: string;
  score?: number;
  tier?: number;
  reason?: string;
  distance?: number;
}

/** One module's answer. A bare suggestion list is accepted too. */
export interface SuggestionAnswer {
  module?: string;
  suggestions?: readonly SuggestionWire[] | null;
}

export type SuggestionList = SuggestionAnswer | readonly SuggestionWire[];

export interface MergedSuggestion {
  text: string;
  source?: string;
  score: number;
  /** 1..5. A missing or unset tier is module (4), as in `effectiveTier`. */
  tier: number;
  /** 0 for a direct suggestion. */
  distance: number;
  reason?: 'correction' | 'synonym';
  module?: string;
}

export interface MergeSuggestionsOptions {
  /** Folds hidden for this caller: the never-suggest words of every layer (own, company, tenant) and language. */
  hidden: ReadonlySet<string>;
  /** Session action: drop this suggestion text. */
  isOwnWord?: (word: string) => boolean;
  /** Session action: drop this suggestion text. */
  isBlocked?: (text: string) => boolean;
  /** The query term the lists answer; `personal` is checked against it. */
  queryTerm?: string;
  /**
   * Folds of the resolved personal dictionary (I-3024): a query term that is
   * in it gets no suggestion at all — no spelling, no correction, no synonym,
   * whichever module answered. The search never corrects a word the user
   * knows.
   */
  personal?: ReadonlySet<string>;
  limit: number;
}

const MODULE_TIER = 4;

function isAnswer(list: SuggestionList): list is SuggestionAnswer {
  return !Array.isArray(list);
}

function reasonOf(value: unknown): 'correction' | 'synonym' | undefined {
  if (value === 'correction' || value === 'synonym') return value;
  return undefined;
}

function tierOf(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 5) return MODULE_TIER;
  return value;
}

function distanceOf(value: unknown, direct: boolean): number {
  if (direct) return 0;
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return value;
}

function readRow(row: SuggestionWire, module: string | undefined): MergedSuggestion | null {
  if (typeof row?.text !== 'string' || row.text === '') return null;
  const score = typeof row.score === 'number' ? row.score : Number.NaN;
  if (!Number.isFinite(score)) return null;
  const reason = reasonOf(row.reason);
  const suggestion: MergedSuggestion = {
    text: row.text,
    score,
    tier: tierOf(row.tier),
    distance: distanceOf(row.distance, reason !== undefined),
  };
  if (typeof row.source === 'string' && row.source) suggestion.source = row.source;
  if (reason) suggestion.reason = reason;
  if (module) suggestion.module = module;
  return suggestion;
}

function rowsOf(list: SuggestionList): MergedSuggestion[] {
  const answer = isAnswer(list) ? list : { suggestions: list };
  const suggestions = answer.suggestions;
  if (!Array.isArray(suggestions)) return [];
  const out: MergedSuggestion[] = [];
  for (const row of suggestions) {
    if (!row || typeof row !== 'object') continue;
    const read = readRow(row, answer.module);
    if (read) out.push(read);
  }
  return out;
}

/**
 * The order of `backend/module/search/rank.go` (`sort.SliceStable`):
 * direct first, then — for a spelling suggestion — distance ascending,
 * then tier ascending, then score descending, then text ascending.
 */
export function compareSuggestions(a: MergedSuggestion, b: MergedSuggestion): number {
  const aDirect = a.reason !== undefined;
  const bDirect = b.reason !== undefined;
  if (aDirect !== bDirect) return aDirect ? -1 : 1;
  if (!aDirect && a.distance !== b.distance) return a.distance - b.distance;
  if (a.tier !== b.tier) return a.tier - b.tier;
  if (a.score !== b.score) return b.score - a.score;
  if (a.text < b.text) return -1;
  if (a.text > b.text) return 1;
  return 0;
}

function kept(item: MergedSuggestion, opts: MergeSuggestionsOptions): boolean {
  const key = fold(item.text).text;
  if (!key) return false;
  if (opts.hidden.has(key)) return false;
  if (opts.isBlocked?.(item.text)) return false;
  if (opts.isOwnWord?.(item.text)) return false;
  return true;
}

/**
 * One order across every module's suggestions. The best of a fold wins, so a
 * word hidden in master leaves a module's suggestion of the same fold too.
 * A failed module passes an empty list; this function never throws.
 */
export function mergeSuggestions(
  lists: readonly SuggestionList[],
  opts: MergeSuggestionsOptions,
): MergedSuggestion[] {
  const limit = Number.isFinite(opts.limit) ? Math.max(0, Math.floor(opts.limit)) : 0;
  if (limit === 0) return [];
  if (opts.queryTerm && opts.personal?.has(fold(opts.queryTerm).text)) return [];
  const pending = lists.flatMap(rowsOf).filter((item) => kept(item, opts));
  pending.sort(compareSuggestions);
  const seen = new Set<string>();
  const out: MergedSuggestion[] = [];
  for (const item of pending) {
    const key = fold(item.text).text;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
}
